import cn from 'classnames';
import { FC, ReactNode } from 'react';

import { LiteratureReference } from '@/shared/api/transcriptor/transcriptorApi';

import {
  CITATION_NUMBER_SEPARATOR,
  CITATION_PATTERN,
  startsWithConsultationEmoji,
} from '../../config';
import { getReferenceUrl, isCitationNumber } from '../../lib';

import styles from './ConsultationContent.module.scss';

type ConsultationContentProps = {
  text: string;
  className?: string;
  literatureReferences?: LiteratureReference[] | null;
};

const renderCitation = (
  match: RegExpMatchArray,
  referenceByIndex: Map<number, LiteratureReference>,
): ReactNode => {
  const [, citationNumbers] = match;
  const offset = match.index ?? 0;

  const citationNodes = citationNumbers
    .split(CITATION_NUMBER_SEPARATOR)
    .map((part, partIndex) => {
      const reference = isCitationNumber(part)
        ? referenceByIndex.get(Number(part))
        : undefined;
      const url = reference ? getReferenceUrl(reference) : null;

      if (!url) {
        return part;
      }

      return (
        <a
          key={`${offset}-${partIndex}`}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.citationReferenceLink}
        >
          {part}
        </a>
      );
    });

  return (
    <span key={offset} className={styles.citationReference}>
      [{citationNodes}]
    </span>
  );
};

const renderCitationText = (
  citationText: string,
  literatureReferences?: LiteratureReference[] | null,
): ReactNode[] => {
  if (!literatureReferences?.length) {
    return [citationText];
  }

  const matches = [...citationText.matchAll(CITATION_PATTERN)];
  if (matches.length === 0) {
    return [citationText];
  }

  const referenceByIndex = new Map(
    literatureReferences.map((reference) => [reference.index, reference]),
  );

  const { nodes, lastIndex } = matches.reduce<{
    nodes: ReactNode[];
    lastIndex: number;
  }>(
    (acc, match) => {
      const offset = match.index ?? 0;
      const precedingText = citationText.slice(acc.lastIndex, offset);

      return {
        nodes: [
          ...acc.nodes,
          ...(precedingText ? [precedingText] : []),
          renderCitation(match, referenceByIndex),
        ],
        lastIndex: offset + match[0].length,
      };
    },
    { nodes: [], lastIndex: 0 },
  );

  const trailingText = citationText.slice(lastIndex);
  return trailingText ? [...nodes, trailingText] : nodes;
};

export const ConsultationContent: FC<ConsultationContentProps> = ({
  text,
  className,
  literatureReferences,
}) => {
  const lines = text.split('\n');
  const emojiLines = lines.filter((line) => startsWithConsultationEmoji(line));
  const hasMultipleEmojiLines = emojiLines.length > 1;

  if (hasMultipleEmojiLines) {
    // Split into separate paragraphs for each emoji line
    return (
      <>
        {lines
          .map((line, lineIndex) => {
            if (line.trim() === '') {
              return null;
            }
            const isEmojiLine = startsWithConsultationEmoji(line);
            return (
              <p
                key={lineIndex}
                className={cn(
                  'p2',
                  styles.text,
                  isEmojiLine && styles.textWithEmoji,
                  className,
                )}
              >
                {renderCitationText(line, literatureReferences)}
              </p>
            );
          })
          .filter(Boolean)}
      </>
    );
  }

  // Render as single paragraph
  return (
    <p
      className={cn(
        'p2',
        styles.text,
        startsWithConsultationEmoji(text) && styles.textWithEmoji,
        className,
      )}
    >
      {renderCitationText(text, literatureReferences)}
    </p>
  );
};
